var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { AvailabilityUtility, DexfSettingsUtility, PriceUtility, ProductDescriptionProductMeasureEnum, ProductDescriptionUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { ProductIdentifier as SkapaProductIdentifier } from '@ingka/product-identifier-webc/passive.js';
import ProductRowUtility from '../../utilities/ProductRowUtility.js';
import CompactProductRowStyle from './CompactProductRowStyle.js';
import LowInStockStatusDot from './LowInStockStatusDot.js';
/**
 * Compact Product row component.
 */
export default class CompactProductRow extends KompisElement {
    constructor() {
        super(...arguments);
        // Stores.
        this.stores = null;
        // Selected store id.
        this.storeId = null;
        // Store availabilities.
        this.storeAvailabilities = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        const product = this.shoppingProduct.product;
        const storeAvailability = AvailabilityUtility.getStoreAvailabilityForProduct({
            product: product,
            storeAvailabilities: this.storeAvailabilities
        });
        return html ` <style>
				${CompactProductRowStyle.getStyle({
            theme: this.theme
        })}
			</style>
			<div class="compactWrapper">
				${ProductRowUtility.getQuantityField({
            shoppingProduct: this.shoppingProduct,
            isCompactLayout: true,
            translations: this.getTranslations()
        })}
				<div>
					${this.getProductNameAndDescription(product)}${this.getLowInStockStoreAvailabilityStatusIndicator(storeAvailability)}
				</div>
				${ProductRowUtility.getLocation({
            storeAvailability,
            displayStoreAvailability: true,
            isCompactLayout: true,
            translations: this.getTranslations()
        })}
				${ProductRowUtility.getArticleNumber({
            product,
            isSubtle: false,
            translations: this.getTranslations()
        })}
				${this.getWeight(product)} ${this.getCompactPrice({ quantity: 1, product })}
				${this.getCompactPrice(this.shoppingProduct)}
			</div>`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-product-identifier', SkapaProductIdentifier);
        this.customElements.define('kompis-price-formatter', PriceFormatter);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getProductNameAndDescription(product) {
        return html `
			<p class="productNameAndDescription text--body-s">
				<span class="productName">${product.content?.name}</span>
				<span class="productDescription">${this.getProductDescription(product)}</span>
			</p>
		`;
    }
    getProductDescription(product) {
        const description = ProductDescriptionUtility.getDescription({
            settings: this.getSettings(),
            translations: this.getTranslations(),
            product,
            include: {
                productMeasures: ProductDescriptionProductMeasureEnum.short,
                designText: true,
                measurementSystem: this.measurementSystem
            }
        });
        return description;
    }
    getLowInStockStoreAvailabilityStatusIndicator(storeAvailability) {
        const store = this.stores?.find((store) => store.id === this.storeId) || null;
        if (!storeAvailability || !this.storeId || !this.stores || !store) {
            return null;
        }
        const statusColor = AvailabilityUtility.getStoreAvailabilityStatusColor(storeAvailability);
        const lowInStockColor = 'orange';
        if (!statusColor || statusColor !== lowInStockColor) {
            return null;
        }
        const statusLabel = AvailabilityUtility.getStoreAvailabilityLabel({
            translations: this.getTranslations(),
            storeAvailability
        })
            ?.replace('[[store]]', store.name ?? '')
            .replace('{{store}}', store.name ?? '');
        // Custom solution instead of using skapa-status component, since skapa-status does not support
        // the half filled orange circle in the small size we want for print.
        return html `
			<p class="lowInStockStoreStatus text--body-s">
				${this.getLowInStockStatusDot()}${statusLabel}
			</p>
		`;
    }
    getLowInStockStatusDot() {
        const statusDotSpan = document.createElement('span');
        statusDotSpan.innerHTML = LowInStockStatusDot;
        return statusDotSpan;
    }
    getWeight(product) {
        const productWeight = ProductDescriptionUtility.getProductWeight({
            settings: this.getSettings(),
            translations: this.getTranslations(),
            product,
            include: {
                measurementSystem: this.measurementSystem
            }
        });
        return html `<p class="weight text--body-s">${productWeight}</p>`;
    }
    getCompactPrice(shoppingProduct) {
        const priceInformation = shoppingProduct.product.content?.priceInformation;
        if (priceInformation) {
            const totalPrice = TotalPriceCalculator.calculate([shoppingProduct], this.getSettings().localisation.dateFormat);
            const isSingleProduct = shoppingProduct.quantity === 1;
            const { priceInfoIkeaFamilyPrice, priceInfoRegularPrice, productListTotalPriceSummary } = this.getTranslations();
            const rowUnitPrice = totalPrice.inclTax;
            const rowTotalPrice = PriceUtility.getPrice(totalPrice);
            if (totalPrice.family) {
                return html `
					<div class="compactPriceWrapper">
						${!isSingleProduct
                    ? html ` <span class="visuallyHidden">${productListTotalPriceSummary}. </span> `
                    : null}
						<p class="compactCommercialMessage text--body-s">${priceInfoIkeaFamilyPrice}</p>
						<p class="compactPrice text--body-s">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${rowTotalPrice}"
							></kompis-price-formatter>
						</p>
						<p class="compactCommercialMessage text--body-s">${priceInfoRegularPrice}</p>
						<p class="compactPrice text--body-s">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${rowUnitPrice}"
							></kompis-price-formatter>
						</p>
					</div>
				`;
            }
            return html `
				<div class="compactPriceWrapper">
					<span class="visuallyHidden"
						>${isSingleProduct ? priceInfoRegularPrice : productListTotalPriceSummary}</span
					>
					<kompis-price-formatter
						.settings="${this.settings}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${rowTotalPrice}"
					></kompis-price-formatter>
				</div>
			`;
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], CompactProductRow.prototype, "theme", void 0);
__decorate([
    property({ type: Object, required: true })
], CompactProductRow.prototype, "shoppingProduct", void 0);
__decorate([
    property({ type: Object })
], CompactProductRow.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], CompactProductRow.prototype, "storeId", void 0);
__decorate([
    property({ type: Object })
], CompactProductRow.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: Object })
], CompactProductRow.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], CompactProductRow.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], CompactProductRow.prototype, "settings", void 0);
__decorate([
    property({ type: String })
], CompactProductRow.prototype, "measurementSystem", void 0);
//# sourceMappingURL=CompactProductRow.js.map